import {CommonModule} from '@angular/common';
import {Component, computed, input, output, viewChild, ChangeDetectionStrategy} from '@angular/core';
import {RouterModule} from '@angular/router';
import {SeFeEmptyStateModule} from 'se-fe-empty-state';
import {SeFeLinkModule} from 'se-fe-link';
import {SeFePaginationChange, SeFePaginationModule, SeFePaginationOptions} from 'se-fe-pagination';
import {SeFeSpinnerModule} from 'se-fe-spinner';
import {InitalSort, SeFeTableDataSource, SeFeTableModule, SeFeTableSortDirective, SortChangeEvent} from 'se-fe-table';
import {SeFeToolbarModule} from 'se-fe-toolbar';
import {CatalogsQueryParams, CatalogSummary, DEFAULT_CATALOGS_QUERY_PARAMS} from '../../../models/clare';
import {SeFeIconModule} from 'se-fe-icon';
import {SeFeButtonModule} from 'se-fe-button';

@Component({
  selector: 'app-catalog-table',
  templateUrl: './catalog-table.component.html',
  styleUrls: ['./catalog-table.component.scss'],
  host: {class: 'app-catalog-table'},
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    CommonModule,
    RouterModule,
    SeFeEmptyStateModule,
    SeFeLinkModule,
    SeFePaginationModule,
    SeFeSpinnerModule,
    SeFeTableModule,
    SeFeToolbarModule,
    SeFeIconModule,
    SeFeButtonModule,
  ],
})
export class CatalogTableComponent {
  queryChanged = output<Partial<CatalogsQueryParams>>();
  createCatalogRequested = output<HTMLElement>();
  deleteCatalogRequested = output<{catalog: CatalogSummary; trigger: HTMLElement}>();

  sorter = viewChild.required(SeFeTableSortDirective);

  catalogs = input<CatalogSummary[]>([]);
  loading = input.required<boolean>();
  paginationOptions = input<SeFePaginationOptions>();
  query = input<string | undefined>();
  sortBy = input<CatalogsQueryParams['order_by'] | undefined>();
  sortDirection = input<CatalogsQueryParams['direction'] | undefined>();

  displayedColumns = ['Name', 'Number of Products', 'Created At', 'Delete'];

  datasource = computed(() => new SeFeTableDataSource(this.catalogs()));
  showSearchToolbar = computed(() => this.catalogs().length > 0 || Boolean(this.query()?.trim()));
  initialSort = computed((): InitalSort => ({
    column: this.sortBy() || '',
    direction: this.sortDirection() ?? null,
  }));

  handleSearch(queryString: string): void {
    this.queryChanged.emit({
      page: 1,
      search: queryString.trim(),
    });
  }

  requestCreateCatalog(event: Event): void {
    this.createCatalogRequested.emit(event.currentTarget as HTMLElement);
  }

  requestDeleteCatalog(catalog: CatalogSummary, event: Event): void {
    event.stopPropagation();
    this.deleteCatalogRequested.emit({catalog, trigger: event.currentTarget as HTMLElement});
  }

  paginationChange(event: SeFePaginationChange): void {
    const queryParams: Partial<CatalogsQueryParams> = {
      page: event.currentPage,
      per_page: event.limit,
    };

    try {
      const currentSorter = this.sorter();
      const currentSortColumn = currentSorter?.active;
      const currentSortDirection = currentSorter?.direction;

      if (currentSortColumn && currentSortDirection && currentSortDirection !== '') {
        queryParams.order_by = currentSortColumn;
        queryParams.direction = currentSortDirection as CatalogsQueryParams['direction'];
      }
    } catch {
      if (this.sortBy() && this.sortDirection()) {
        queryParams.order_by = this.sortBy();
        queryParams.direction = this.sortDirection();
      }
    }

    this.queryChanged.emit(queryParams);
  }

  sortChange(event: SortChangeEvent): void {
    if (!event || event.initial) return;

    this.queryChanged.emit({
      page: 1,
      per_page: this.paginationOptions()?.limit ?? DEFAULT_CATALOGS_QUERY_PARAMS.per_page,
      order_by: event.direction ? event.column : undefined,
      direction: event.direction ? (event.direction as CatalogsQueryParams['direction']) : undefined,
    });
  }

  getCatalogLink(catalog: CatalogSummary): string {
    return `./${catalog.id}`;
  }
}
